import { useRef } from 'react';
import { format } from 'date-fns';
import { Trash2 } from 'lucide-react';
import { MobileBottomSheet } from '@/components/ui/MobileBottomSheet';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileTextarea } from '@/components/ui/MobileTextarea';
import { MobileQuantityStepper } from '@/components/ui/MobileQuantityStepper';
import { formatInr } from '@/app/lib/pricing';
import type { CartLine } from '../useOrderCart';
import type { PricedItem } from '@/app/types/item';
import type { Party } from '@/app/types/party';
import { cn } from '@/lib/utils';

interface ReviewOrderSheetProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isSubmitting: boolean;
    party: Party | null;
    lines: CartLine[];
    grandTotal: number;
    onQuantityChange: (item: PricedItem, quantity: number) => void;
    deliveryDate: string;
    onDeliveryDateChange: (value: string) => void;
    notes: string;
    onNotesChange: (value: string) => void;
}

const isoOffsetDays = (days: number): string => {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return format(d, 'yyyy-MM-dd');
};

const labelForDate = (iso: string): string => {
    if (!iso) return 'Not set';
    if (iso === isoOffsetDays(0)) return 'Today';
    if (iso === isoOffsetDays(1)) return 'Tomorrow';
    try {
        return format(new Date(`${iso}T00:00:00`), 'dd MMM yyyy');
    } catch {
        return iso;
    }
};

/**
 * Final review before creating the order.
 *
 * Quantities stay editable here so a correction found while reading the list back
 * to the shopkeeper doesn't require dismissing the sheet.
 */
export function ReviewOrderSheet({
    open,
    onClose,
    onConfirm,
    isSubmitting,
    party,
    lines,
    grandTotal,
    onQuantityChange,
    deliveryDate,
    onDeliveryDateChange,
    notes,
    onNotesChange,
}: ReviewOrderSheetProps) {
    const dateInputRef = useRef<HTMLInputElement>(null);

    const dateOption = (label: string, iso: string) => {
        const active = deliveryDate === iso;
        return (
            <button
                type="button"
                onClick={() => onDeliveryDateChange(active ? '' : iso)}
                aria-pressed={active}
                className={cn(
                    'flex-1 h-10 rounded-lg text-sm font-medium border transition-colors',
                    active
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-secondary text-secondary-foreground border-transparent active:bg-accent'
                )}
            >
                {label}
            </button>
        );
    };

    return (
        <MobileBottomSheet open={open} onClose={onClose} title="Review order" heightMode="tall">
            <div className="flex flex-col h-full">
                <div className="flex-1 overflow-y-auto">
                    {party && (
                        <div className="px-4 py-3 border-b border-border">
                            <p className="text-xs text-muted-foreground">Customer</p>
                            <p className="font-semibold text-foreground">{party.name}</p>
                        </div>
                    )}

                    <ul className="divide-y divide-border">
                        {lines.map((line) => (
                            <li key={line.item.id} className="flex items-center gap-3 px-4 py-3">
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-foreground leading-snug break-words">
                                        {line.item.name}
                                    </p>
                                    <p className="text-sm text-muted-foreground mt-0.5 tabular-nums">
                                        {formatInr(line.rate)} / {line.item.unitName}
                                        {line.tierLabel && (
                                            <span className="text-green-500 ml-2 text-[11px] font-medium">
                                                {line.tierLabel}
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-sm font-semibold text-foreground mt-1 tabular-nums">
                                        {formatInr(line.totals.totalAmount)}
                                    </p>
                                </div>

                                <MobileQuantityStepper
                                    value={line.quantity}
                                    onValueChange={(q) => onQuantityChange(line.item, q)}
                                    aria-label={line.item.name}
                                />

                                <button
                                    type="button"
                                    aria-label={`Remove ${line.item.name}`}
                                    onClick={() => onQuantityChange(line.item, 0)}
                                    className="flex items-center justify-center w-9 h-9 rounded-full text-muted-foreground active:bg-accent flex-shrink-0"
                                >
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </li>
                        ))}
                    </ul>

                    <section className="px-4 pt-5">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                            Deliver by
                        </h3>
                        <div className="flex gap-2">
                            {dateOption('Today', isoOffsetDays(0))}
                            {dateOption('Tomorrow', isoOffsetDays(1))}
                            <button
                                type="button"
                                onClick={() => {
                                    dateInputRef.current?.showPicker?.();
                                    dateInputRef.current?.focus();
                                }}
                                className={cn(
                                    'flex-1 h-10 rounded-lg text-sm font-medium border transition-colors',
                                    deliveryDate &&
                                        deliveryDate !== isoOffsetDays(0) &&
                                        deliveryDate !== isoOffsetDays(1)
                                        ? 'bg-primary text-primary-foreground border-primary'
                                        : 'bg-secondary text-secondary-foreground border-transparent active:bg-accent'
                                )}
                            >
                                {deliveryDate &&
                                deliveryDate !== isoOffsetDays(0) &&
                                deliveryDate !== isoOffsetDays(1)
                                    ? labelForDate(deliveryDate)
                                    : 'Pick date'}
                            </button>
                            <input
                                ref={dateInputRef}
                                type="date"
                                value={deliveryDate}
                                min={isoOffsetDays(0)}
                                onChange={(e) => onDeliveryDateChange(e.target.value)}
                                className="sr-only"
                                aria-label="Expected delivery date"
                            />
                        </div>
                    </section>

                    <section className="px-4 pt-5 pb-6">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                            Notes (optional)
                        </h3>
                        <MobileTextarea
                            placeholder="Anything the admin should know..."
                            value={notes}
                            onChange={(e) => onNotesChange(e.target.value)}
                        />
                    </section>
                </div>

                <div
                    className="border-t border-border bg-background flex-shrink-0"
                    style={{ padding: '12px 16px 16px' }}
                >
                    <div className="flex items-baseline justify-between mb-3">
                        <span className="text-sm text-muted-foreground">
                            {lines.length} {lines.length === 1 ? 'item' : 'items'}
                        </span>
                        <span className="text-2xl font-bold text-foreground tabular-nums">
                            {formatInr(grandTotal)}
                        </span>
                    </div>
                    <MobileButton
                        type="button"
                        size="lg"
                        className="w-full"
                        disabled={lines.length === 0 || isSubmitting}
                        isLoading={isSubmitting}
                        onClick={onConfirm}
                    >
                        Create Order
                    </MobileButton>
                </div>
            </div>
        </MobileBottomSheet>
    );
}
